import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookMarked, ChevronRight, Pencil, Plus, Send, SlidersHorizontal, Trash2, TriangleAlert, Undo2 } from 'lucide-react';
import {
  useListExamsQuery,
  useCreateExamMutation,
  useUpdateExamMutation,
  usePublishExamMutation,
  useUnpublishExamMutation,
  useListSubjectsQuery,
  useCreateSubjectMutation,
  useUpdateSubjectMutation,
  useDeleteSubjectMutation,
  useDeleteExamMutation,
  useListExamGroupsQuery,
  useGetGradingScaleQuery,
  getApiError,
  type ExamSummary,
  type Subject,
  type GradeBand,
  type ExamKind,
  EXAM_KIND_LABEL,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Field';
import { GradingScaleDialog } from './GradingScaleDialog';
import { GradeBandsEditor, bandsAreUsable, sortBands } from './GradeBandsEditor';
import { Mark } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const TERMS = [
  { value: 'term_1', label: 'First term' },
  { value: 'term_2', label: 'Second term' },
  { value: 'term_3', label: 'Third term' },
] as const;

const termLabel = (t: string) => TERMS.find((x) => x.value === t)?.label ?? t;

/** 2026 in September becomes "2026/2027" — the way a school writes it. */
function currentAcademicYear(): string {
  const now = new Date();
  const start = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}/${start + 1}`;
}

/*
  ═════════════════════════════════════════════════════════════════════════════
  EXAMS

  Two things live here because they are one job done twice a year: the subjects
  a school teaches, and the rounds of assessment it runs over them.

  The column that matters is `status`
  ───────────────────────────────────
  A draft is being marked. A published exam is on parents' phones. Those are
  not two shades of the same thing, and the screen does not present them as a
  toggle in a form — publishing is its own button, behind its own confirmation,
  because it is the moment a whole school's results become somebody else's
  information.

  Why marks entered is shown as a count and not a percentage
  ──────────────────────────────────────────────────────────
  A percentage needs a denominator — children × subjects × classes — that is
  wrong the moment a child is enrolled or a subject added mid-term, and a
  progress bar that reads 103% is worse than no bar. The count is a fact.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function ExamsPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const role = useAuthStore((s) => s.user?.role);
  const isOffice = role === 'institution_admin' || role === 'super_admin';
  const navigate = useNavigate();

  const { data: exams = [], isLoading, isError, error } = useListExamsQuery(institutionId, { skip: !institutionId });
  const { data: subjects = [] } = useListSubjectsQuery(
    { institutionId, all: true },
    { skip: !institutionId },
  );
  const liveSubjects = subjects.filter((s) => s.isActive);
  const { data: groups = [] } = useListExamGroupsQuery(institutionId, { skip: !institutionId });
  /* What a NEW exam starts from. Each exam then keeps its own copy. */
  const { data: schoolScale } = useGetGradingScaleQuery(institutionId, { skip: !institutionId });

  const [createExam, createState] = useCreateExamMutation();
  const [updateExam, updateExamState] = useUpdateExamMutation();
  const [publishExam, publishState] = usePublishExamMutation();
  const [unpublishExam, unpublishState] = useUnpublishExamMutation();
  const [createSubject, subjectState] = useCreateSubjectMutation();
  const [updateSubject, updateSubjectState] = useUpdateSubjectMutation();
  const [deleteSubject, deleteSubjectState] = useDeleteSubjectMutation();
  const [deleteExam, deleteExamState] = useDeleteExamMutation();

  const [examOpen, setExamOpen] = useState(false);
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [pending, setPending] = useState<null | { exam: ExamSummary; action: 'publish' | 'unpublish' | 'delete' }>(null);
  /* The subject being renamed in place, and the one awaiting a delete. */
  const [editingSubject, setEditingSubject] = useState<null | { _id: string; name: string; code: string }>(null);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  /* Set while editing an existing exam; null while creating a new one. */
  const [editingExamId, setEditingExamId] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    academicYear: currentAcademicYear(),
    term: 'term_1',
    kind: 'end_of_term' as ExamKind,
    groupIds: [] as string[],
    classScoreWeight: 30,
    examScoreWeight: 70,
    markScale: 'weight' as 'weight' | 'percent',
    gradeBands: [] as GradeBand[],
  });
  const [subjectForm, setSubjectForm] = useState({ name: '', code: '' });

  const weightsOk = form.classScoreWeight + form.examScoreWeight === 100;
  const mutationError =
    createState.error ??
    updateExamState.error ??
    publishState.error ??
    unpublishState.error ??
    deleteExamState.error;

  /*
    One dialog for both, because the fields are the same and a second copy of
    them is a second place for them to drift apart.
  */
  const openExamForEdit = (exam: ExamSummary) => {
    setEditingExamId(exam._id);
    setForm({
      name: exam.name,
      academicYear: exam.academicYear,
      term: exam.term,
      kind: exam.kind,
      groupIds: exam.groupIds,
      classScoreWeight: exam.classScoreWeight,
      examScoreWeight: exam.examScoreWeight,
      markScale: exam.markScale,
      /* The exam's OWN bands, not the school's — this is where the promise
         about history becomes visible: an old exam opens showing what it was
         actually marked against. */
      gradeBands: sortBands(exam.gradeBands),
    });
    setExamOpen(true);
  };

  const openExamForCreate = () => {
    setEditingExamId(null);
    setForm({
      name: '',
      academicYear: currentAcademicYear(),
      term: 'term_1',
      kind: 'end_of_term',
      groupIds: [],
      classScoreWeight: 30,
      examScoreWeight: 70,
      markScale: 'weight',
      /* Starts from the school's current scale, and diverges from it the
         moment anybody edits it here. */
      gradeBands: sortBands(schoolScale ?? []),
    });
    setExamOpen(true);
  };

  const submitExam = async () => {
    const result = editingExamId
      ? await updateExam({ institutionId, examId: editingExamId, ...form })
      : await createExam({ institutionId, ...form });
    if (!('error' in result)) {
      setExamOpen(false);
      setEditingExamId(null);
    }
  };

  const submitSubject = async () => {
    const result = await createSubject({ institutionId, ...subjectForm });
    if (!('error' in result)) setSubjectForm({ name: '', code: '' });
  };

  const columns: Column<ExamSummary>[] = [
    {
      key: 'name',
      header: 'Exam',
      render: (e) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{e.name}</p>
          <p className={styles.meta}>
            {EXAM_KIND_LABEL[e.kind]} · {termLabel(e.term)} · {e.academicYear}
          </p>
        </div>
      ),
    },
    {
      key: 'weighting',
      header: 'Weighting',
      render: (e) => {
        /* Highest and lowest grade, so an exam marked on an older scale is
           visible at a glance rather than only on opening it. */
        const scale = sortBands(e.gradeBands);
        return (
          <div className="min-w-0">
            <p className="truncate tabular-nums text-muted-foreground">
              {e.markScale === 'weight'
                ? `class /${e.classScoreWeight} · exam /${e.examScoreWeight}`
                : `${e.classScoreWeight}% class · ${e.examScoreWeight}% exam`}
            </p>
            {scale.length > 0 && (
              <p className={cn(styles.meta, 'truncate')}>
                graded {scale[0]!.grade}–{scale[scale.length - 1]!.grade}
              </p>
            )}
          </div>
        );
      },
      className: 'w-[190px] max-sm:hidden',
    },
    {
      key: 'marks',
      header: 'Marks entered',
      align: 'num',
      render: (e) => <span className="tabular-nums text-foreground">{e.marksEntered}</span>,
      className: 'w-[120px]',
    },
    {
      key: 'status',
      header: 'Status',
      render: (e) =>
        e.status === 'published' ? (
          <Mark tone="success">With parents</Mark>
        ) : (
          <Mark tone="warning">Being marked</Mark>
        ),
      className: 'w-[130px]',
    },
    {
      key: 'actions',
      header: '',
      align: 'num',
      render: (e) => (
        <div className="flex justify-end gap-1.5">
          {isOffice && (
            <>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Edit ${e.name}`}
                title={
                  e.status === 'published'
                    ? 'Take it back from parents before changing it'
                    : 'Edit name, term, classes and weighting'
                }
                disabled={e.status === 'published'}
                onClick={() => openExamForEdit(e)}
              >
                <Pencil size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Delete ${e.name}`}
                disabled={e.status === 'published'}
                title={
                  e.status === 'published'
                    ? 'Parents can see these report cards — take it back from them first'
                    : e.marksEntered > 0
                      ? `Deletes the exam and its ${e.marksEntered} mark${e.marksEntered === 1 ? '' : 's'}`
                      : `Delete ${e.name}`
                }
                onClick={() => setPending({ exam: e, action: 'delete' })}
              >
                <Trash2 size={13} />
              </Button>
            </>
          )}
          {isOffice &&
            (e.status === 'published' ? (
              <Button variant="secondary" size="sm" onClick={() => setPending({ exam: e, action: 'unpublish' })}>
                <Undo2 size={12} className="mr-1" />
                Take back
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                disabled={e.marksEntered === 0}
                title={e.marksEntered === 0 ? 'No marks have been entered yet' : undefined}
                onClick={() => setPending({ exam: e, action: 'publish' })}
              >
                <Send size={12} className="mr-1" />
                Publish
              </Button>
            ))}
          <Button variant="secondary" size="sm" onClick={() => navigate(`/exams/${e._id}`)}>
            Marks
            <ChevronRight size={12} className="ml-0.5" />
          </Button>
        </div>
      ),
      className: 'w-[280px]',
    },
  ];

  return (
    <PageLayout
      title="Exam results"
      subtitle="Record marks by subject and term, then release report cards to parents"
      breadcrumbs={[{ label: 'Academics' }, { label: 'Exam results' }]}
      contained={false}
      actions={
        <div className="flex gap-2">
          {/* Readable by teachers, editable by the office — the dialog takes
              `canEdit` rather than being hidden, because a teacher wanting to
              know what counts as an A should be able to look it up. */}
          <Button variant="secondary" size="sm" onClick={() => setScaleOpen(true)}>
            <SlidersHorizontal size={13} className="mr-1" />
            Grading
          </Button>
          {isOffice && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSubjectsOpen(true)}>
              <BookMarked size={13} className="mr-1" />
              Subjects
              <span className="ml-1.5 tabular-nums opacity-60">{liveSubjects.length}</span>
            </Button>
          <Button variant="primary" size="sm" onClick={openExamForCreate} disabled={liveSubjects.length === 0}>
            <Plus size={13} className="mr-1" />
            New exam
          </Button>
          </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/* Subjects are the columns of every mark sheet, so there is nothing to
            mark until at least one exists. Said once, here, rather than left
            for somebody to discover on an empty grid. */}
        {isOffice && liveSubjects.length === 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3.5 py-3">
            <TriangleAlert size={15} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
            <div>
              <p className="text-[12.5px] font-bold text-[hsl(var(--record-ink-warning))]">
                No subjects yet
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]/85">
                Subjects are the columns of every mark sheet. Add them before creating an exam —
                Mathematics, English, Integrated Science, and whatever else this school teaches.
              </p>
              <Button variant="secondary" size="sm" className="mt-2" onClick={() => setSubjectsOpen(true)}>
                Add subjects
              </Button>
            </div>
          </div>
        )}

        {mutationError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/25 bg-destructive/[0.06] px-3 py-2.5 text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]"
          >
            <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
            {getApiError(mutationError)}
          </div>
        )}

        <div className="overflow-clip rounded-lg border border-border bg-card">
          <DataTable
            columns={columns}
            rows={exams}
            rowKey={(e) => e._id}
            loading={isLoading}
            error={isError || undefined}
            errorMessage={isError ? getApiError(error) : undefined}
            emptyTitle="No exams yet"
            emptyDescription={
              isOffice
                ? 'Create one for this term, then teachers can enter marks class by class.'
                : 'Nothing has been set up for marking yet. The office creates exams.'
            }
          />
        </div>
      </div>

      {/* ── New exam ─────────────────────────────────────────────────────── */}
      <Modal
        open={examOpen}
        onClose={() => setExamOpen(false)}
        title={editingExamId ? 'Edit exam' : 'New exam'}
        description={
          editingExamId
            ? 'Changing how marks are entered, or the weights, is refused once marks exist — those numbers are not convertible.'
            : 'Marks can be entered as soon as it exists. Nothing reaches parents until you publish it.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setExamOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={createState.isLoading || updateExamState.isLoading}
              disabled={!form.name.trim() || !weightsOk || !bandsAreUsable(form.gradeBands)}
              onClick={() => void submitExam()}
            >
              {editingExamId ? 'Save changes' : 'Create exam'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Name" htmlFor="exam-name" required hint="What a parent will see on the report card.">
            <Input
              id="exam-name"
              placeholder="End of First Term Examination"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>

          {/*
            Beside the term rather than buried, because "which term" and
            "which assessment within it" are one thought — a school sitting a
            mid-term and an end-of-term paper is answering both at once.
          */}
          <Field label="Which assessment" htmlFor="exam-kind" required>
            <Select
              id="exam-kind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as ExamKind })}
            >
              <option value="end_of_term">End of term — the terminal report</option>
              <option value="mid_term">Mid-term — sat partway through the term</option>
              <option value="mock">Mock — a rehearsal before a public exam</option>
              <option value="other">Other</option>
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Academic year" htmlFor="exam-year" required>
              <Input
                id="exam-year"
                value={form.academicYear}
                onChange={(e) => setForm({ ...form, academicYear: e.target.value })}
              />
            </Field>
            <Field label="Term" htmlFor="exam-term" required>
              <Select id="exam-term" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })}>
                {TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {/*
            The weighting is per exam on purpose: Ghanaian schools do not agree
            on it, thirty/seventy and forty/sixty are both normal, and a school
            may run one split for the lower classes and another for the upper.
          */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Class score weight" htmlFor="w-class" required>
              <Input
                id="w-class"
                type="number"
                min={0}
                max={100}
                value={form.classScoreWeight}
                onChange={(e) =>
                  setForm({
                    ...form,
                    classScoreWeight: Number(e.target.value),
                    examScoreWeight: 100 - Number(e.target.value),
                  })
                }
              />
            </Field>
            <Field label="Exam weight" htmlFor="w-exam" required>
              <Input
                id="w-exam"
                type="number"
                min={0}
                max={100}
                value={form.examScoreWeight}
                onChange={(e) =>
                  setForm({
                    ...form,
                    examScoreWeight: Number(e.target.value),
                    classScoreWeight: 100 - Number(e.target.value),
                  })
                }
              />
            </Field>
          </div>
          <p className={cn(styles.meta, !weightsOk && 'text-[hsl(var(--record-ink-danger))]')}>
            {weightsOk
              ? 'The two must add up to 100. How a teacher types them in is the next question.'
              : `These add up to ${form.classScoreWeight + form.examScoreWeight}. They must add up to 100.`}
          </p>

          {/*
            The question a head teacher can actually answer: what do your
            teachers have written down? Not "which arithmetic do you prefer".
            The example under each option is the whole explanation.
          */}
          <Field label="How teachers enter marks" htmlFor="mark-scale" required>
            <div className="flex flex-col gap-1.5" id="mark-scale">
              {([
                {
                  value: 'weight' as const,
                  title: 'Out of the weights',
                  example: `Class /${form.classScoreWeight} + exam /${form.examScoreWeight}, added together`,
                  note: 'What a mark sheet already holds. No conversion.',
                },
                {
                  value: 'percent' as const,
                  title: 'As percentages',
                  example: 'Class /100 and exam /100, combined by the weights',
                  note: 'The teacher converts each mark first.',
                },
              ]).map((option) => {
                const on = form.markScale === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setForm({ ...form, markScale: option.value })}
                    aria-pressed={on}
                    className={cn(
                      'rounded-md border px-3 py-2 text-left transition-colors',
                      on ? 'border-primary bg-primary/[0.07]' : 'border-border hover:bg-muted',
                    )}
                  >
                    <span className="block text-[12.5px] font-bold text-foreground">{option.title}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{option.example}</span>
                    <span className="block text-[11px] text-muted-foreground/80">{option.note}</span>
                  </button>
                );
              })}
            </div>
          </Field>

          {/*
            ── This exam's grading ───────────────────────────────────────────

            Started from the school's scale and kept as this exam's own copy
            for good. That copy is why a child graded A last year still reads
            A after the school moves its boundaries — so changing it here
            changes THIS exam's report cards and nothing else, which is the
            opposite of what the Grading button does.
          */}
          <Field
            label="Grading for this exam"
            htmlFor="exam-bands"
            hint={
              editingExamId
                ? 'What this exam was marked against. Changing it re-grades its own marks and leaves every other exam alone.'
                : 'Started from the school scale. Change it here and only this exam is affected.'
            }
          >
            <div id="exam-bands">
              <GradeBandsEditor
                compact
                bands={form.gradeBands}
                onChange={(gradeBands) => setForm({ ...form, gradeBands })}
              />
            </div>
          </Field>

          <Field
            label="Classes"
            htmlFor="exam-groups"
            hint="Leave every one unticked if the whole school sits it."
          >
            <div className="flex flex-wrap gap-1.5" id="exam-groups">
              {groups.map((g) => {
                const on = form.groupIds.includes(g._id);
                return (
                  <button
                    key={g._id}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        groupIds: on ? form.groupIds.filter((x) => x !== g._id) : [...form.groupIds, g._id],
                      })
                    }
                    className={cn(
                      'rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors',
                      on
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </Modal>

      {/* ── Subjects ─────────────────────────────────────────────────────── */}
      <Modal
        open={subjectsOpen}
        onClose={() => setSubjectsOpen(false)}
        title="Subjects"
        description="The columns of every mark sheet. Retiring one keeps it on the report cards it already appears on."
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setSubjectsOpen(false)}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-2">
            <Field label="Subject" htmlFor="sub-name" className="flex-1">
              <Input
                id="sub-name"
                placeholder="Integrated Science"
                value={subjectForm.name}
                onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
              />
            </Field>
            <Field label="Short" htmlFor="sub-code" className="w-[90px]">
              <Input
                id="sub-code"
                placeholder="SCI"
                maxLength={12}
                value={subjectForm.code}
                onChange={(e) => setSubjectForm({ ...subjectForm, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Button
              variant="primary"
              className="mb-[2px]"
              loading={subjectState.isLoading}
              disabled={!subjectForm.name.trim() || !subjectForm.code.trim()}
              onClick={() => void submitSubject()}
            >
              Add
            </Button>
          </div>

          {subjectState.error && (
            <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
              {getApiError(subjectState.error)}
            </p>
          )}

          <div className="flex flex-col divide-y divide-border overflow-clip rounded-md border border-border">
            {subjects.length === 0 ? (
              <p className="px-3 py-4 text-center text-[11.5px] text-muted-foreground">
                No subjects yet.
              </p>
            ) : (
              subjects.map((s) => {
                const editing = editingSubject?._id === s._id;
                /* The flag. A delete that is merely greyed out tells somebody
                   they cannot, without telling them why — and the why here is
                   both specific and actionable. */
                const locked = s.marksRecorded > 0;

                if (editing) {
                  return (
                    <div key={s._id} className="flex items-center gap-2 bg-muted/40 px-3 py-2">
                      <Input
                        aria-label="Short code"
                        className="w-[76px]"
                        maxLength={12}
                        value={editingSubject.code}
                        onChange={(e) =>
                          setEditingSubject({ ...editingSubject, code: e.target.value.toUpperCase() })
                        }
                      />
                      <Input
                        aria-label="Subject name"
                        className="min-w-0 flex-1"
                        value={editingSubject.name}
                        onChange={(e) => setEditingSubject({ ...editingSubject, name: e.target.value })}
                      />
                      <Button
                        variant="primary"
                        size="sm"
                        loading={updateSubjectState.isLoading}
                        disabled={!editingSubject.name.trim() || !editingSubject.code.trim()}
                        onClick={() => {
                          void updateSubject({
                            institutionId,
                            subjectId: s._id,
                            name: editingSubject.name.trim(),
                            code: editingSubject.code.trim(),
                          });
                          setEditingSubject(null);
                        }}
                      >
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingSubject(null)}>
                        Cancel
                      </Button>
                    </div>
                  );
                }

                return (
                  <div key={s._id} className={cn('flex items-center gap-2 px-3 py-2', !s.isActive && 'opacity-55')}>
                    <span className="w-[52px] flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-center text-[10px] font-bold tracking-wide text-muted-foreground">
                      {s.code}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-foreground">
                      {s.name}
                    </span>

                    {!s.isActive && <Mark tone="neutral">Retired</Mark>}
                    {locked && (
                      <Mark tone="info">
                        {s.marksRecorded} mark{s.marksRecorded === 1 ? '' : 's'}
                      </Mark>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Rename ${s.name}`}
                      title="Rename — corrects a typo on every report card it appears on"
                      onClick={() => setEditingSubject({ _id: s._id, name: s.name, code: s.code })}
                    >
                      <Pencil size={13} />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        void updateSubject({ institutionId, subjectId: s._id, isActive: !s.isActive })
                      }
                      title={
                        s.isActive
                          ? 'Takes it off new mark sheets and leaves old report cards alone'
                          : 'Put it back on new mark sheets'
                      }
                    >
                      {s.isActive ? 'Retire' : 'Restore'}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Delete ${s.name}`}
                      disabled={locked}
                      title={
                        locked
                          ? `${s.marksRecorded} mark${s.marksRecorded === 1 ? ' is' : 's are'} recorded against this subject — retire it instead, or those report cards lose their column`
                          : `Delete ${s.name}`
                      }
                      onClick={() => setSubjectToDelete(s)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          {deleteSubjectState.error && (
            <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
              {getApiError(deleteSubjectState.error)}
            </p>
          )}
        </div>
      </Modal>

      {/* ── Publish / take back ──────────────────────────────────────────── */}
      <GradingScaleDialog
        open={scaleOpen}
        onClose={() => setScaleOpen(false)}
        institutionId={institutionId}
        canEdit={isOffice}
      />

      <ConfirmDialog
        open={subjectToDelete !== null}
        onClose={() => setSubjectToDelete(null)}
        loading={deleteSubjectState.isLoading}
        title={`Delete ${subjectToDelete?.name}?`}
        confirmLabel="Delete the subject"
        onConfirm={() => {
          if (subjectToDelete) void deleteSubject({ institutionId, subjectId: subjectToDelete._id });
          setSubjectToDelete(null);
        }}
        body={
          <>
            Nothing is recorded against {subjectToDelete?.name}, so deleting it loses no marks.
            <br />
            <br />
            If the school simply stopped teaching it, <strong className="text-foreground">retire</strong>{' '}
            it instead — that keeps it on the report cards it already appears on.
          </>
        }
      />

      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        loading={publishState.isLoading || unpublishState.isLoading}
        destructive={pending?.action !== 'publish'}
        title={
          pending?.action === 'publish'
            ? `Publish ${pending.exam.name}?`
            : pending?.action === 'delete'
              ? `Delete ${pending.exam.name}?`
              : `Take ${pending?.exam.name} back from parents?`
        }
        confirmLabel={
          pending?.action === 'publish'
            ? 'Publish to parents'
            : pending?.action === 'delete'
              ? 'Delete the exam'
              : 'Take back'
        }
        onConfirm={() => {
          if (!pending) return;
          const args = { institutionId, examId: pending.exam._id };
          if (pending.action === 'publish') void publishExam(args);
          else if (pending.action === 'unpublish') void unpublishExam(args);
          else void deleteExam(args);
          setPending(null);
        }}
        body={
          pending?.action === 'delete' ? (
            <>
              {pending.exam.marksEntered > 0 ? (
                <>
                  This deletes {pending.exam.name} and the{' '}
                  <strong className="text-foreground">
                    {pending.exam.marksEntered} mark
                    {pending.exam.marksEntered === 1 ? '' : 's'}
                  </strong>{' '}
                  entered against it. There is no undo, and nothing is kept.
                </>
              ) : (
                <>
                  This deletes {pending.exam.name}. No marks have been entered against it, so
                  nothing else goes with it.
                </>
              )}
              <br />
              <br />
              If the exam was real and only the setup was wrong, edit it instead.
            </>
          ) : pending?.action === 'publish' ? (
            <>
              Every parent at this school will be able to see their own child's report card for{' '}
              <strong className="text-foreground">{pending.exam.name}</strong> — marks, grades and
              position — the moment you do this.
              <br />
              <br />
              {pending.exam.marksEntered} mark{pending.exam.marksEntered === 1 ? '' : 's'} have been
              entered. A subject nobody has marked yet simply will not appear, so check the classes
              are finished before releasing.
              <br />
              <br />
              Marks are frozen once published. You can take it back to correct them.
            </>
          ) : (
            <>
              Parents will stop seeing <strong className="text-foreground">{pending?.exam.name}</strong>{' '}
              immediately, and marks become editable again.
              <br />
              <br />
              Anyone who already opened their child's report card has seen those numbers. If they are
              changing, that is worth a word rather than a silent correction.
            </>
          )
        }
      />
    </PageLayout>
  );
}

